/**
 * Pure helpers for shaping GCP Error Reporting log entries.
 *
 * Cloud Run captures single-line JSON written to stdout/stderr as structured
 * log entries. Error Reporting automatically ingests entries that have
 * severity ERROR and carry the ReportedErrorEvent @type — grouping them by
 * stack trace (or, when no stack is available, by context.reportLocation).
 * https://cloud.google.com/error-reporting/docs/formatting-error-messages
 *
 * Everything in this file is side-effect free so it can be unit tested;
 * emission lives in ./server.ts, browser capture in ./client.ts.
 */

export const REPORTED_ERROR_EVENT_TYPE =
  "type.googleapis.com/google.devtools.clouderrorreporting.v1beta1.ReportedErrorEvent";

/** Reject client-error request bodies larger than this before parsing. */
export const MAX_BODY_BYTES = 16_384;

const MAX_MESSAGE_LENGTH = 2_000;
const MAX_STACK_LENGTH = 8_000;
const MAX_URL_LENGTH = 1_000;
const MAX_DIGEST_LENGTH = 128;
const MAX_RELEASE_LENGTH = 64;

export const CLIENT_ERROR_SOURCES = [
  "window.onerror",
  "unhandledrejection",
  "error-boundary",
  "global-error-boundary",
] as const;

export type ClientErrorSource = (typeof CLIENT_ERROR_SOURCES)[number];

export interface ClientErrorPayload {
  message: string;
  stack?: string;
  source: ClientErrorSource;
  digest?: string;
  url?: string;
  release?: string;
}

export interface ErrorEntryInput {
  message: string;
  stack?: string;
  /** Where the error was captured; used as reportLocation when no stack exists. */
  source: string;
  service: string;
  version: string;
  digest?: string;
  httpRequest?: {
    method?: string;
    url?: string;
    userAgent?: string;
  };
  extra?: Record<string, unknown>;
}

/**
 * Normalize any thrown value into message/stack/digest parts.
 * Next.js attaches a `digest` to errors thrown during server rendering.
 */
export function extractErrorDetails(error: unknown): {
  message: string;
  stack?: string;
  digest?: string;
} {
  if (error instanceof Error) {
    return {
      message: `${error.name}: ${error.message}`,
      stack: error.stack,
      digest:
        "digest" in error && typeof error.digest === "string" ? error.digest : undefined,
    };
  }
  if (typeof error === "string") return { message: error };
  try {
    return { message: `Non-error thrown: ${JSON.stringify(error)}` };
  } catch {
    return { message: `Non-error thrown: ${String(error)}` };
  }
}

/**
 * Build a structured log entry that GCP Error Reporting ingests.
 *
 * When a stack is available it becomes the `message` (Chrome stacks already
 * embed the message; Firefox/Safari stacks don't, so we prepend it). Without
 * a stack, Error Reporting requires context.reportLocation to accept the
 * event, so the capture source stands in as the location.
 */
export function buildErrorEntry(input: ErrorEntryInput): Record<string, unknown> {
  const stack = input.stack?.trim();
  const message = stack
    ? stack.includes(input.message.slice(0, 200))
      ? stack
      : `${input.message}\n${stack}`
    : input.message;

  const httpRequest = input.httpRequest
    ? Object.fromEntries(
        Object.entries(input.httpRequest).filter(([, v]) => v !== undefined),
      )
    : undefined;

  return {
    severity: "ERROR",
    "@type": REPORTED_ERROR_EVENT_TYPE,
    message,
    serviceContext: { service: input.service, version: input.version },
    context: {
      ...(httpRequest && Object.keys(httpRequest).length > 0 ? { httpRequest } : {}),
      ...(stack
        ? {}
        : {
            reportLocation: {
              functionName: input.source,
              filePath: "unknown",
              lineNumber: 0,
            },
          }),
    },
    source: input.source,
    ...(input.digest ? { digest: input.digest } : {}),
    ...input.extra,
  };
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function optionalString(value: unknown, max: number): string | undefined {
  return typeof value === "string" && value.trim().length > 0
    ? truncate(value.trim(), max)
    : undefined;
}

/**
 * Validate and sanitize an untrusted client-error request body.
 * Returns null when the payload is not usable; all strings are length-capped
 * so a hostile client can't inflate log volume.
 */
export function parseClientErrorPayload(raw: unknown): ClientErrorPayload | null {
  if (typeof raw !== "object" || raw === null) return null;
  const body = raw as Record<string, unknown>;

  const message = optionalString(body.message, MAX_MESSAGE_LENGTH);
  if (!message) return null;

  const source = CLIENT_ERROR_SOURCES.find((s) => s === body.source);
  if (!source) return null;

  return {
    message,
    source,
    stack: optionalString(body.stack, MAX_STACK_LENGTH),
    digest: optionalString(body.digest, MAX_DIGEST_LENGTH),
    url: optionalString(body.url, MAX_URL_LENGTH),
    release: optionalString(body.release, MAX_RELEASE_LENGTH),
  };
}
